<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  stages: {
    type: Array,
    required: true,
  },
  currentStageId: {
    type: String,
    required: true,
  },
  isWon: {
    type: Boolean,
    default: false,
  },
  isLost: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['moveToStage', 'requestTerminalMove']);

const { t } = useI18n();

// ── Derived ───────────────────────────────────────────────────────────────────

const currentStage = computed(
  () => props.stages.find(s => s.id === props.currentStageId) ?? null
);

const currentPosition = computed(() => currentStage.value?.position ?? 0);

const totalStages = computed(() => props.stages.length);

const funnelPercent = computed(() => {
  if (totalStages.value === 0) return 0;
  return Math.round((currentPosition.value / totalStages.value) * 100);
});

// ── Stage state helpers ───────────────────────────────────────────────────────

const isCompleted = stage =>
  stage.position < currentPosition.value && !props.isWon && !props.isLost;

const isCurrent = stage => stage.id === props.currentStageId;

// ── Node visual classes ───────────────────────────────────────────────────────

const nodeClass = stage => {
  if (props.isWon) {
    return 'bg-green-500 text-white border-green-500';
  }
  if (props.isLost) {
    if (stage.position <= currentPosition.value) {
      return 'bg-red-500 text-white border-red-500';
    }
    return 'bg-n-alpha-2 text-n-slate-9 border-n-weak';
  }
  if (isCompleted(stage)) {
    return 'bg-green-500 text-white border-green-500';
  }
  if (isCurrent(stage)) {
    const color = stage.color;
    return color
      ? `border-2 border-current text-current`
      : 'bg-n-brand text-white border-n-brand';
  }
  return 'bg-n-alpha-2 text-n-slate-9 border-n-weak';
};

const nodeStyle = stage => {
  if (isCurrent(stage) && stage.color && !props.isWon && !props.isLost) {
    return {
      color: stage.color,
      backgroundColor: stage.color + '22',
      borderColor: stage.color,
    };
  }
  return {};
};

// ── Connector line classes ────────────────────────────────────────────────────

const connectorClass = stage => {
  if (props.isWon) return 'border-green-500';
  if (props.isLost) {
    if (stage.position < currentPosition.value) return 'border-red-500';
    return 'border-n-weak';
  }
  if (stage.position < currentPosition.value) return 'border-n-brand';
  return 'border-n-weak';
};

// ── Stage label ───────────────────────────────────────────────────────────────

const stageLabel = stage => {
  if (isCurrent(stage)) {
    if (props.isWon)
      return `${stage.name} (${t('PIPELINE.STEPPER.WON_BADGE')})`;
    if (props.isLost)
      return `${stage.name} (${t('PIPELINE.STEPPER.LOST_BADGE')})`;
  }
  return stage.name;
};

// ── Click handler ─────────────────────────────────────────────────────────────

const handleStageClick = stage => {
  if (props.disabled) return;
  if (isCurrent(stage)) return;
  if (stage.terminal) {
    emit('requestTerminalMove', stage.id);
  } else {
    emit('moveToStage', stage.id, props.currentStageId);
  }
};

// ── Aria label ────────────────────────────────────────────────────────────────

const ariaLabel = stage => t('PIPELINE.STEPPER.MOVE_TO', { stage: stage.name });
</script>

<template>
  <div class="flex flex-col gap-2 w-full">
    <!-- ── Stepper track ── -->
    <div class="overflow-x-auto">
      <div class="flex items-start min-w-max px-1 py-2">
        <template v-for="(stage, index) in stages" :key="stage.id">
          <!-- ── Stage node + label ── -->
          <div class="flex flex-col items-center gap-1.5 relative">
            <!-- Node button -->
            <button
              type="button"
              :data-stage-id="stage.id"
              class="w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-1"
              :class="[
                nodeClass(stage),
                {
                  'cursor-default': isCurrent(stage) || disabled,
                  'cursor-pointer hover:opacity-80':
                    !isCurrent(stage) && !disabled,
                  'ring-4 ring-n-brand/20 animate-pulse':
                    isCurrent(stage) && !isWon && !isLost && !stage.color,
                },
              ]"
              :style="nodeStyle(stage)"
              :aria-label="ariaLabel(stage)"
              :aria-current="isCurrent(stage) ? 'step' : undefined"
              :aria-disabled="disabled || undefined"
              @click="handleStageClick(stage)"
            >
              <!-- Checkmark for completed / won / lost-past -->
              <svg
                v-if="
                  isCompleted(stage) ||
                  isWon ||
                  (isLost && stage.position <= currentPosition)
                "
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>

              <!-- Current stage dot -->
              <span
                v-else-if="isCurrent(stage)"
                class="w-3 h-3 rounded-full bg-current"
                aria-hidden="true"
              />

              <!-- Future stage number -->
              <span
                v-else
                class="text-xs font-medium leading-none"
                aria-hidden="true"
              >
                {{ index + 1 }}
              </span>
            </button>

            <!-- Stage label -->
            <span
              class="text-xs text-center leading-tight max-w-[80px] truncate"
              :class="[
                isCurrent(stage)
                  ? 'font-medium text-n-slate-12'
                  : 'text-n-slate-10',
                isWon && isCurrent(stage) ? 'text-green-600' : '',
                isLost && isCurrent(stage) ? 'text-red-600' : '',
              ]"
              :title="stageLabel(stage)"
            >
              {{ stageLabel(stage) }}
            </span>

            <!-- Current indicator -->
            <div
              v-if="isCurrent(stage)"
              class="flex flex-col items-center gap-0.5 -mt-0.5"
            >
              <!-- Upward triangle -->
              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="currentColor"
                class="text-n-brand"
                aria-hidden="true"
              >
                <path d="M5 0L10 6H0L5 0Z" />
              </svg>
              <span class="text-[10px] font-medium text-n-brand leading-none">
                {{ t('PIPELINE.STEPPER.CURRENT') }}
              </span>
            </div>
          </div>

          <!-- ── Connector line (not after last node) ── -->
          <div
            v-if="index < stages.length - 1"
            class="flex-1 h-0 mt-5 border-t-2 min-w-[24px]"
            :class="connectorClass(stage)"
            aria-hidden="true"
          />
        </template>
      </div>
    </div>

    <!-- ── Funnel progress ── -->
    <div v-if="totalStages > 0" class="flex justify-end px-1">
      <span class="text-xs text-n-slate-10">
        {{ t('PIPELINE.STEPPER.FUNNEL_PROGRESS', { percent: funnelPercent }) }}
      </span>
    </div>
  </div>
</template>
